import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { ChatApiResponse, ChatMessage, WidgetConfig } from "./types";
import { getStrings } from "./i18n";
import { responseWineCard } from "./wine-mapper";
import { WineCard } from "./WineCard";

interface Props {
  config: WidgetConfig;
}

function newId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function newSessionId(): string {
  return "mw_widget_" + newId();
}

export function ChatInterface({ config }: Props) {
  const t = useMemo(() => getStrings(config.lang), [config.lang]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionIdRef = useRef<string>(newSessionId());
  const contextTokenRef = useRef<string | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Scroll to the sentinel div immediately and keep tracking via ResizeObserver
  // on the *inner content* div (not the fixed-size scroll container) so it
  // fires whenever the WineCard or any message grows in height.
  useEffect(() => {
    const bottom = bottomRef.current;
    const content = contentRef.current;
    if (!bottom) return;

    const scrollToBottom = () => {
      bottom.scrollIntoView({ behavior: "instant", block: "end" });
    };

    // Immediate scroll on new message
    scrollToBottom();

    if (!content) return;

    // Re-scroll while content grows (e.g. Framer Motion card animation).
    // Stop after 1 s — animations are done well before then.
    let active = true;
    const observer = new ResizeObserver(() => {
      if (active) scrollToBottom();
    });
    observer.observe(content);

    const stop = setTimeout(() => {
      active = false;
      observer.disconnect();
    }, 1000);

    return () => {
      active = false;
      observer.disconnect();
      clearTimeout(stop);
    };
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;

    // No apiKey → demo mode: the server answers anonymously as the public
    // Sommelier (same behavior as the website's demo chat).
    const loadingId = newId();
    setMessages((prev) => [
      ...prev,
      { role: "user", id: newId(), content: trimmed },
      { role: "loading", id: loadingId },
    ]);
    setInput("");
    setBusy(true);

    try {
      const res = await fetch(config.apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(config.apiKey ? { "x-api-key": config.apiKey } : {}),
        },
        body: JSON.stringify({
          message: trimmed,
          sessionId: sessionIdRef.current,
          contextToken: contextTokenRef.current,
        }),
      });

      if (!res.ok) {
        let detail = "";
        try {
          const body = (await res.json()) as { message?: string; error?: { message?: string } };
          detail = body?.error?.message ?? body?.message ?? "";
        } catch {
          /* ignore */
        }
        throw new Error(detail || `HTTP ${res.status}`);
      }

      const json = (await res.json()) as { data?: ChatApiResponse } & ChatApiResponse;
      const data: ChatApiResponse = json.data ?? json;
      contextTokenRef.current = data.contextToken;
      // Only the server's consent-gated card may be rendered. Search matches
      // are not card permission and may contain unrelated low-score candidates.
      const wine = responseWineCard(data);

      setMessages((prev) =>
        prev
          .filter((m) => m.id !== loadingId)
          .concat({
            role: "assistant",
            id: newId(),
            content: data.reply || "",
            wine,
          }),
      );
    } catch (err) {
      const message =
        err instanceof Error && err.message ? err.message : t.errorNetwork;
      setMessages((prev) =>
        prev
          .filter((m) => m.id !== loadingId)
          .concat({
            role: "assistant",
            id: newId(),
            content: message,
            error: true,
          }),
      );
    } finally {
      setBusy(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }

  return (
    <>
      <div className="mw-messages" ref={scrollRef}>
        <div ref={contentRef} className="mw-messages-inner">
          {messages.length === 0 && (
            <div className="mw-empty">
              <div className="mw-empty-mark">W</div>
              <h3>{t.emptyHeading}</h3>
              <p>{t.emptySub}</p>
              <div className="mw-suggestions">
                {t.suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="mw-suggestion"
                    onClick={() => send(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((msg) => {
              if (msg.role === "loading") {
                return (
                  <motion.div
                    key={msg.id}
                    className="mw-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <span className="mw-spinner" />
                    {t.loading}
                  </motion.div>
                );
              }
              const isUser = msg.role === "user";
              const isError = msg.role === "assistant" && msg.error;
              return (
                <motion.div
                  key={msg.id}
                  className={`mw-msg ${isUser ? "mw-msg-user" : "mw-msg-assistant"} ${isError ? "mw-error" : ""}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {msg.role === "assistant" && msg.wine && (
                    <WineCard data={msg.wine} />
                  )}
                  {msg.content && (
                    <div className="mw-bubble-text">{msg.content}</div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
          <div ref={bottomRef} style={{ height: 0, flexShrink: 0 }} />
        </div>
      </div>

      <form
        className="mw-input-row"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <input
          ref={inputRef}
          className="mw-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t.placeholder}
          disabled={busy}
          aria-label={t.placeholder}
        />
        <button
          type="submit"
          className="mw-send"
          disabled={busy || !input.trim()}
          aria-label={t.send}
          title={t.send}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </form>
    </>
  );
}
